import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';

export interface WorldState {
  currentWorldId: string;
  unlockedWorlds: string[];
  worldProgress: Record<string, {
    level: number;
    unlockedAreas: string[];
    defeatedBosses: string[];
    completedQuests: string[];
  }>;
  dimensionRiftProgress: number; // 0-100の値で次元の亀裂の開放度を表す
}

const initialState: WorldState = {
  currentWorldId: 'fantasy', // 剣と魔法の世界
  unlockedWorlds: ['fantasy'],
  worldProgress: {
    fantasy: {
      level: 1,
      unlockedAreas: ['village'],
      defeatedBosses: [],
      completedQuests: [],
    },
  },
  dimensionRiftProgress: 0,
};

export const worldSlice: Slice<WorldState> = createSlice({
  name: 'world',
  initialState,
  reducers: {
    changeWorld: (state, action: PayloadAction<string>) => {
      const worldId = action.payload;
      if (state.unlockedWorlds.includes(worldId)) {
        state.currentWorldId = worldId;
      }
    },
    unlockWorld: (state, action: PayloadAction<string>) => {
      const worldId = action.payload;
      if (!state.unlockedWorlds.includes(worldId)) {
        state.unlockedWorlds.push(worldId);
        
        // 新しい世界の初期プログレスを設定
        state.worldProgress[worldId] = {
          level: 1,
          unlockedAreas: ['start'], // 各世界の最初のエリア
          defeatedBosses: [],
          completedQuests: [],
        };
      }
    },
    increaseWorldLevel: (state, action: PayloadAction<{ worldId: string; amount: number }>) => {
      const { worldId, amount } = action.payload;
      if (state.worldProgress[worldId]) {
        state.worldProgress[worldId].level += amount;
      }
    },
    unlockArea: (state, action: PayloadAction<{ worldId: string; areaId: string }>) => {
      const { worldId, areaId } = action.payload;
      if (state.worldProgress[worldId] && !state.worldProgress[worldId].unlockedAreas.includes(areaId)) {
        state.worldProgress[worldId].unlockedAreas.push(areaId);
      }
    },
    defeatBoss: (state, action: PayloadAction<{ worldId: string; bossId: string }>) => {
      const { worldId, bossId } = action.payload;
      if (state.worldProgress[worldId] && !state.worldProgress[worldId].defeatedBosses.includes(bossId)) {
        state.worldProgress[worldId].defeatedBosses.push(bossId);
      }
    },
    completeQuest: (state, action: PayloadAction<{ worldId: string; questId: string }>) => {
      const { worldId, questId } = action.payload;
      if (state.worldProgress[worldId] && !state.worldProgress[worldId].completedQuests.includes(questId)) {
        state.worldProgress[worldId].completedQuests.push(questId);
      }
    },
    increaseDimensionRiftProgress: (state, action: PayloadAction<number>) => {
      state.dimensionRiftProgress = Math.min(100, state.dimensionRiftProgress + action.payload);
    },
    resetDimensionRiftProgress: (state) => {
      state.dimensionRiftProgress = 0;
    },
    // 転生時の処理
    transcend: (state, action: PayloadAction<{ targetWorldId: string }>) => {
      const { targetWorldId } = action.payload;
      
      // 転生先の世界に移動
      state.currentWorldId = targetWorldId;
      
      // ディメンションリフトの進行度リセット
      state.dimensionRiftProgress = 0;
    },
  },
});

export const {
  changeWorld,
  unlockWorld,
  increaseWorldLevel,
  unlockArea,
  defeatBoss,
  completeQuest,
  increaseDimensionRiftProgress,
  resetDimensionRiftProgress,
  transcend
} = worldSlice.actions;

export const worldReducer = worldSlice.reducer; 